// Send people back to where they were after signing in (e.g. the suggestion
// they tried to vote on). /login?redirect=<path> stores the path in
// sessionStorage, which survives the Discord OAuth round trip in the same tab;
// login.vue and confirm.vue take it once sign-in completes.

const STORAGE_KEY = 'mychar:post-login-redirect'
const MAX_AGE_MS = 30 * 60 * 1000

/** Internal app path only: "/x", never "//host" or "/\host" (protocol-relative). */
export function isSafeRedirectPath(path: unknown): path is string {
  return typeof path === 'string' && path.startsWith('/') && !/^\/[/\\]/.test(path)
}

/** Login URL that returns to `path` afterwards. */
export function loginPathFor(path: string): string {
  return `/login?redirect=${encodeURIComponent(path)}`
}

/** Remember the redirect, or forget a stale one when `path` is not usable. */
export function rememberPostLoginRedirect(path: unknown) {
  try {
    if (isSafeRedirectPath(path)) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ path, at: Date.now() }))
    } else {
      sessionStorage.removeItem(STORAGE_KEY)
    }
  } catch {
    // Storage unavailable (private mode): fall back to the default landing page
  }
}

/** The remembered path (once), or null. */
export function takePostLoginRedirect(): string | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    sessionStorage.removeItem(STORAGE_KEY)
    if (!raw) return null
    const { path, at } = JSON.parse(raw)
    return isSafeRedirectPath(path) && Date.now() - at < MAX_AGE_MS ? path : null
  } catch {
    return null
  }
}
